<script setup>
import {onMounted, ref} from "vue"
import store from "@/store";
import helpers from "@/lib/helpers";
import TimeclockPunchFormatter from "@/components/timeclock/timeclock-punch-formatter.vue";
import FormDatetimepicker from "@/components/form/form-datetimepicker.vue";

const props = defineProps({
  user: {
    type: [String, Number]
  }
})

const timeclock = ref()
const modal_ref = ref()
const manual = ref()

const fields = [
  {key: 'clock_in_dow', sortable: true, label: 'Day', tdClass: 'text-nowrap align-middle'},
  {key: 'clock_in', sortable: true, label: 'Clocked In', tdClass: 'text-nowrap align-middle'},
  {key: 'clock_out', sortable: true, label: 'Clocked Out', tdClass: 'text-nowrap align-middle'},
  {key: 'total_hours', sortable: true, label: 'Hours', tdClass: 'text-nowrap align-middle text-right'},
]

const refresh = async () => {
  const user = props.user || 'self'
  const result = await helpers.asyncAction(`/api/user/${user}/timeclock`, 'get', {noload: true})
  result.timecard.punches = result.timecard.punches.map(row => {
    return {
      _rowVariant: row.approval_required ? 'warning' : null,
      ...row
    }
  })
  timeclock.value = result
}

onMounted(() => {
  refresh()
})

const def_clock_in = async () => {
  if (!await helpers.confirm('Clock In?'))
    return

  await helpers.asyncAction(timeclock.value._links.clock_in_url)
  helpers.toast('Clocked in')
  refresh()
}

const def_clock_out = async (clock_out_code) => {
  console.log('clock_out_code', clock_out_code)
  if (!await helpers.confirm('Clock Out for ' + (store.state.settings.defaults.clock_out_codes[clock_out_code]?.description || 'Unknown') + '?'))
    return

  if (!timeclock.value.active) {
    helpers.alert('Not clocked in')
    return
  }

  await helpers.asyncAction(timeclock.value.active._links.clock_out_url, {data: {clock_out_code}})
  helpers.toast('Clocked out')
  refresh()
}

const manual_save = async () => {
  const data = {...manual.value}
  data[manual.value.key + '_manual_reason'] = manual.value.reason
  data[manual.value.key] = manual.value.datetime

  delete manual.value.datetime

  const url = manual.value.key === 'clock_in' ? timeclock.value._links.clock_in_url : timeclock.value.active._links.clock_out_url

  await helpers.asyncAction(url, {data})
  helpers.toast(manual.value.key === 'clock_in' ? 'Clocked in' : 'Clocked out')
  refresh()
  manual.value = null
}

const manual_init = (key) => {
  manual.value = {
    datetime: null,
    reason: null,
    clock_out_code: null,
    key
  }
}

const cancel_clockin = async () => {
  if (!await helpers.confirm('Cancel this clock in?'))
    return

  await helpers.asyncAction(timeclock.value.active._links.delete_url)

  refresh()
}

</script>

<template>
  <modal :title="'Timeclock ' + ((user && timeclock && (' - ' + timeclock.user.name)) || '')" height="400px" :large="!manual" ref="modal_ref">
    <template #footer>
      <template v-if="manual">
        <b-button size="sm" variant="outline-secondary" class="px-4 mr-2"
                  @click="manual = null">Cancel
        </b-button>
        <b-button size="sm" @click="manual_save" variant="outline-primary" class="px-4">Save</b-button>
      </template>
      <b-row v-else-if="timeclock" no-gutters class="text-center">
        <b-col cols="4" class="stat-tile">
          <font-awesome-icon :icon="['fas', 'clock']" class="text-primary mb-1"/>
          <div class="h4 mb-0 text-primary">{{ timeclock.timecard.totals.regular_hours | decimal(2) }}</div>
          <div class="text-muted small text-uppercase">Regular</div>
        </b-col>
        <b-col cols="4" class="stat-tile border-left border-right">
          <font-awesome-icon :icon="['fas', 'hourglass-half']"
                             :class="timeclock.timecard.totals.overtime_hours ? 'text-warning' : 'text-muted'" class="mb-1"/>
          <div class="h4 mb-0" :class="timeclock.timecard.totals.overtime_hours ? 'text-warning' : 'text-muted'">
            {{ timeclock.timecard.totals.overtime_hours | decimal(2) }}
          </div>
          <div class="text-muted small text-uppercase">Overtime</div>
        </b-col>
        <b-col cols="4" class="stat-tile">
          <font-awesome-icon :icon="['fas', 'triangle-exclamation']"
                             :class="timeclock.timecard.totals.double_time_hours ? 'text-danger' : 'text-muted'" class="mb-1"/>
          <div class="h4 mb-0" :class="timeclock.timecard.totals.double_time_hours ? 'text-danger' : 'text-muted'">
            {{ timeclock.timecard.totals.double_time_hours | decimal(2) }}
          </div>
          <div class="text-muted small text-uppercase">Double Time</div>
        </b-col>
      </b-row>
    </template>

    <template #top v-if="!manual">
      <template v-if="timeclock && timeclock.active">
        <div class="d-flex align-items-start justify-content-between mb-2">
          <div>
            <b-badge variant="success" pill class="px-2">
              <font-awesome-icon :icon="['fas', 'circle']" class="pulse-dot mr-1"/>
              ON THE CLOCK
            </b-badge>
            <div class="text-muted small mt-1">Since {{ timeclock.active.clock_in | datetz(timeclock.active.timezone) }}</div>
          </div>
          <div class="text-right">
            <div class="h4 mb-0">{{ timeclock.active.elapsed_time }} <span class="text-muted small">hrs</span></div>
            <b-link class="small" @click="cancel_clockin">Cancel Clock In</b-link>
          </div>
        </div>
        <b-alert class="mb-0" show variant="info" v-if="timeclock.active.clock_out">
          <font-awesome-icon :icon="['fas', 'circle-info']" class="mr-1"/>
          You will be clocked out at {{ timeclock.active.clock_out | datetz(timeclock.active.timezone)}}
        </b-alert>
        <b-btn-group class="w-100" v-else>

          <b-btn v-for="(value, key) in $store.state.settings.defaults.clock_out_codes" :key="key"
                 v-b-tooltip.hover :title="'Clock Out for ' + value.description"
                 variant="outline-danger"
                 @click="def_clock_out(key)">
            <font-awesome-icon :icon="['fas', 'right-from-bracket']" class="mr-1"/>
            {{ value.description }}
          </b-btn>

          <b-btn variant="outline-secondary" class="px-3" @click="manual_init('clock_out')" v-b-tooltip.hover title="Manual Clock Out">
            <font-awesome-icon :icon="['fas', 'pencil']"/>
          </b-btn>
        </b-btn-group>
      </template>

      <template v-else-if="timeclock">
        <div class="text-center py-2 mb-2">
          <b-badge variant="secondary" pill class="px-2">NOT CLOCKED IN</b-badge>
        </div>
        <b-btn-group class="w-100">
          <b-btn variant="success" block @click="def_clock_in()">
            <font-awesome-icon :icon="['fas', 'right-to-bracket']" class="mr-1"/>
            Clock In
          </b-btn>
          <b-btn variant="outline-success" class="px-3" @click="manual_init('clock_in')"  v-b-tooltip.hover title="Manual Clock In">
            <font-awesome-icon :icon="['fas', 'pencil']"/>
          </b-btn>
        </b-btn-group>
      </template>
    </template>

    <template v-if="manual">
      <div class="text-muted small mb-3">
        <font-awesome-icon :icon="['fas', 'pencil']" class="mr-1"/>
        Manual {{ manual.key === 'clock_in' ? 'Clock In' : 'Clock Out' }} - a reason is recorded for audit purposes.
      </div>
      <modal-row label="Punch">
        <form-datetimepicker v-model="manual.datetime" :timezone="timeclock.timezone"/>
      </modal-row>
      <modal-row label="Reason" class="mt-2">
        <b-input v-model="manual.reason"/>
      </modal-row>
      <modal-row label="Clock Out Code" class="mt-2" v-if="timeclock.active">
        <b-select v-model="manual.clock_out_code">
          <b-select-option v-for="(value, key) in $store.state.settings.defaults.clock_out_codes" :key="key"
                           :value="key" v-text="value.description"/>

        </b-select>
      </modal-row>
    </template>

    <b-table :fields="fields" :items="timeclock.timecard.punches" head-variant="light" striped hover small
             v-if="!manual && timeclock">
      <template #cell(clock_out)="{item}">
        <timeclock-punch-formatter prefix="clock_out" :value="item"/>
      </template>
      <template #cell(clock_in)="{item}">
        <timeclock-punch-formatter prefix="clock_in" :value="item"/>
      </template>
    </b-table>

  </modal>
</template>

<style scoped>
.stat-tile {
  padding-top: 4px;
  padding-bottom: 4px;
}

.pulse-dot {
  font-size: 0.5em;
  vertical-align: middle;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>